
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';

export interface IntegrationStatus {
  type: string;
  isActive: boolean;
  config?: Record<string, any>;
}

export interface StripeIntegration extends IntegrationStatus {
  webhookSecret?: string | null;
}

export function useIntegrations() {
  const { currentCommunity } = useCommunity();
  
  const { data: integrations, isLoading, error, refetch } = useQuery({
    queryKey: ['community-integrations', currentCommunity?.id],
    queryFn: async () => {
      try {
        if (!currentCommunity?.id) {
          return { 
            stripe: { 
              type: 'stripe', 
              isActive: false 
            } 
          };
        }
        
        // Fetch all integrations for this community
        const { data, error } = await supabase
          .from('community_integrations')
          .select('*')
          .eq('community_id', currentCommunity.id);
        
        if (error) throw error;
        
        // Transform the data into a more usable format
        const result: Record<string, IntegrationStatus> = {};
        
        // Initialize with defaults (all inactive)
        result.stripe = { type: 'stripe', isActive: false };
        
        // Update with actual data if available
        if (data) {
          data.forEach(integration => {
            // Ensure config is treated as a Record<string, any>
            const configData = integration.config as Record<string, any>;
            
            result[integration.integration_type] = {
              type: integration.integration_type,
              isActive: integration.is_active || false,
              config: configData || {}
            };
          });
        }
        
        return result;
      } catch (error) {
        console.error('Error fetching integrations:', error);
        return { stripe: { type: 'stripe', isActive: false } };
      }
    },
    enabled: !!currentCommunity?.id
  });
  
  return {
    integrations: integrations || { stripe: { type: 'stripe', isActive: false } },
    isLoading,
    error,
    refetch,
    isStripeActive: integrations?.stripe?.isActive || false,
    // Add easy access to webhook secret
    stripeWebhookSecret: (integrations?.stripe?.config?.webhook_secret as string) || null
  };
}
